/**
 * Composable for managing document pagination state
 *
 * This composable is shared between:
 * - nmed-doc-search-filter-store
 * - projects-store
 * - saved-documents-store
 */

import type { ComputedRef } from 'vue';
import type { PaginationState, SortConfig, SortDirection } from '@/types/nmed/shared-filter-types';
import { computed, reactive } from 'vue';
import { createEmptyPaginationState, createEmptySortConfig } from '@/types/nmed/shared-filter-types';

/* ---------------------------------------------------------------------------------------------- */
/*                                           TYPES                                                */
/* ---------------------------------------------------------------------------------------------- */

export interface UseDocumentPaginationOptions {
   /**
    * Number of items per page (default: 30)
    */
   itemsPerPage?: number;
}

export interface UseDocumentPaginationReturn {
   /**
    * The reactive pagination state
    */
   pagination: PaginationState;

   /**
    * The reactive sort configuration
    */
   sortConfig: SortConfig;

   /**
    * Whether there are more pages after the current page
    */
   canGoNext: ComputedRef<boolean>;

   /**
    * Whether there are pages before the current page
    */
   canGoPrev: ComputedRef<boolean>;

   /**
    * Resets pagination to page 1
    */
   resetPagination: () => void;

   /**
    * Updates pagination after receiving API response
    * @param totalCount - Total number of items
    * @param filteredCount - Number of items after filtering (optional, defaults to totalCount)
    */
   updatePagination: (totalCount: number, filteredCount?: number) => void;

   /**
    * Sets the current page
    */
   setCurrentPage: (page: number) => void;

   /**
    * Gets the first row index for API pagination (0-indexed)
    */
   getFirstRow: () => number;

   /**
    * Sets the sort configuration
    */
   setSortConfig: (field: string, direction: SortDirection) => void;

   /**
    * Resets sort to default (no sorting)
    */
   resetSort: () => void;

   /**
    * Resets all pagination and sort state
    */
   $reset: () => void;
}

/* ---------------------------------------------------------------------------------------------- */
/*                                       COMPOSABLE                                               */
/* ---------------------------------------------------------------------------------------------- */

export function useDocumentPagination(
   options: UseDocumentPaginationOptions = {},
): UseDocumentPaginationReturn {
   const { itemsPerPage = 30 } = options;

   const pagination = reactive<PaginationState>(createEmptyPaginationState(itemsPerPage));
   const sortConfig = reactive<SortConfig>(createEmptySortConfig());

   /**
    * Whether there are more pages after the current page
    */
   const canGoNext = computed(() => {
      return pagination.currentPage < pagination.totalPages;
   });

   /**
    * Whether there are pages before the current page
    */
   const canGoPrev = computed(() => {
      return pagination.currentPage > 1;
   });

   /**
    * Resets pagination to page 1
    */
   function resetPagination(): void {
      pagination.currentPage = 1;
   }

   /**
    * Updates pagination after receiving API response
    */
   function updatePagination(totalCount: number, filteredCount?: number): void {
      pagination.totalCount = totalCount;
      pagination.filteredCount = filteredCount ?? totalCount;
      pagination.totalPages = Math.ceil((filteredCount ?? totalCount) / pagination.itemsPerPage);

      // Clamp currentPage to valid range (same logic as setCurrentPage)
      if (pagination.currentPage < 1) {
         pagination.currentPage = 1;
      }
      else if (pagination.totalPages > 0 && pagination.currentPage > pagination.totalPages) {
         pagination.currentPage = pagination.totalPages;
      }
   }

   /**
    * Sets the current page
    */
   function setCurrentPage(page: number): void {
      if (page >= 1 && (pagination.totalPages === 0 || page <= pagination.totalPages)) {
         pagination.currentPage = page;
      }
   }

   /**
    * Gets the first row index for API pagination (0-indexed)
    */
   function getFirstRow(): number {
      return (pagination.currentPage - 1) * pagination.itemsPerPage;
   }

   /**
    * Sets the sort configuration
    */
   function setSortConfig(field: string, direction: SortDirection): void {
      sortConfig.field = field;
      sortConfig.direction = direction;
   }

   /**
    * Resets sort to default (no sorting)
    */
   function resetSort(): void {
      sortConfig.field = '';
      sortConfig.direction = 'ASC';
   }

   /**
    * Resets all pagination and sort state
    */
   function $reset(): void {
      pagination.currentPage = 1;
      pagination.totalCount = 0;
      pagination.totalPages = 0;
      pagination.filteredCount = 0;

      sortConfig.field = '';
      sortConfig.direction = 'ASC';
   }

   return {
      pagination,
      sortConfig,
      canGoNext,
      canGoPrev,
      resetPagination,
      updatePagination,
      setCurrentPage,
      getFirstRow,
      setSortConfig,
      resetSort,
      $reset,
   };
}
